import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AlumniContributionForm.css";

axios.defaults.withCredentials = true;

const AlumniContributionForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contribute: "",
    alumni: "",
    batch: "",
    branch: "",
    phone: "",
    email: "",
    fundType: "",
    contributionAmount: "",

    // Own
    aadhar: "",
    ownPan: "",

    // CSR
    companyName: "",
    contactPerson: "",
    contactPersonEmail: "",
    csrPan: "",

    installment: 1,
    emiSchedule: [{ date: "", amount: "" }],
  });

  const [errors, setErrors] = useState({});
  const [noContributeSubmitted, setNoContributeSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [alumniInfo, setAlumniInfo] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState("");

  // -------------------------------------------------- //
  // Fetch logged-in user's alumni details               //
  // -------------------------------------------------- //

  useEffect(() => {
    const fetchAlumniDetails = async () => {
      setDetailsLoading(true);
      setDetailsError("");
      try {
        const response = await axios.get("/backend/alumni/me"); // adjust to your real endpoint
        setAlumniInfo(response.data);
      } catch (error) {
        console.error("Error fetching alumni details:", error);
        setDetailsError("Unable to load your details. Please try again later.");
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchAlumniDetails();
  }, []);

  // -------------------------------------------------- //
  // Validation helpers                                 //
  // -------------------------------------------------- //

  const AADHAR_REGEX = /^\d{12}$/;
  const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateField = (name, value) => {
    switch (name) {
      case "contributionAmount":
        if (!value.toString().trim()) return "Contribution amount is required";
        if (isNaN(value) || Number(value) <= 0)
          return "Enter a valid amount greater than 0";
        return "";

      case "fundType":
        if (!value) return "Please select a fund type";
        return "";

      case "aadhar":
        if (!value.trim()) return "Aadhar number is required";
        if (!AADHAR_REGEX.test(value)) return "Enter a valid 12-digit Aadhar number";
        return "";

      case "ownPan":
      case "csrPan":
        if (!value.trim()) return "PAN number is required";
        if (!PAN_REGEX.test(value.toUpperCase()))
          return "Enter a valid PAN (e.g. ABCDE1234F)";
        return "";

      case "companyName":
        if (!value.trim()) return "Company name is required";
        return "";

      case "contactPerson":
        if (!value.trim()) return "Contact person is required";
        return "";

      case "contactPersonEmail":
        if (!value.trim()) return "Contact person email is required";
        if (!EMAIL_REGEX.test(value)) return "Enter a valid email address";
        return "";

      default:
        return "";
    }
  };

  const validateEMI = (emi) => {
    const emiErrors = {};
    if (!emi.date) emiErrors.date = "Date is required";
    if (!emi.amount.toString().trim()) {
      emiErrors.amount = "Amount is required";
    } else if (isNaN(emi.amount) || Number(emi.amount) <= 0) {
      emiErrors.amount = "Enter a valid amount greater than 0";
    }
    return emiErrors;
  };

  // -------------------------------------------------- //
  // Handlers                                            //
  // -------------------------------------------------- //

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "aadhar") {
      value = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "ownPan" || name === "csrPan") {
      value = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10);
    } else if (name === "contributionAmount") {
      value = value.replace(/\D/g, "");
    } else if (name === "contactPerson") {
      value = value.replace(/[^a-zA-Z\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name] || errors.emiScheduleSum) {
      setErrors((prev) => ({ ...prev, [name]: "", emiScheduleSum: "" }));
    }

    if (name === "contribute") {
      setNoContributeSubmitted(false);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const message = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: message }));
  };

  const handleInstallmentChange = (e) => {
    const count = Number(e.target.value);
    setFormData((prev) => ({
      ...prev,
      installment: count,
      emiSchedule: Array.from({ length: count }, (_, i) => ({
        date: prev.emiSchedule[i]?.date || "",
        amount: prev.emiSchedule[i]?.amount || "",
      })),
    }));
    setErrors((prev) => ({ ...prev, emiSchedule: [] }));
  };

  const handleEMIChange = (index, field, value) => {
    const updated = [...formData.emiSchedule];
    if (field === "amount") value = value.replace(/\D/g, "");
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, emiSchedule: updated }));

    setErrors((prev) => {
      const updatedEmiErrors = [...(prev.emiSchedule || [])];
      if (updatedEmiErrors[index]) {
        updatedEmiErrors[index] = { ...updatedEmiErrors[index], [field]: "" };
      }
      return { ...prev, emiSchedule: updatedEmiErrors, emiScheduleSum: "" };
    });
  };

  const handleEMIBlur = (index, field, value) => {
    setErrors((prev) => {
      const updatedEmiErrors = [...(prev.emiSchedule || [])];
      const emi = formData.emiSchedule[index];
      const fieldErrors = validateEMI({ ...emi, [field]: value });
      updatedEmiErrors[index] = { ...updatedEmiErrors[index], [field]: fieldErrors[field] || "" };
      return { ...prev, emiSchedule: updatedEmiErrors };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    ["contributionAmount", "fundType"].forEach((field) => {
      const message = validateField(field, formData[field]);
      if (message) newErrors[field] = message;
    });

    if (formData.fundType === "OWN") {
      ["aadhar", "ownPan"].forEach((field) => {
        const message = validateField(field, formData[field]);
        if (message) newErrors[field] = message;
      });
    }

    if (formData.fundType === "CSR") {
      ["companyName", "contactPerson", "contactPersonEmail", "csrPan"].forEach((field) => {
        const message = validateField(field, formData[field]);
        if (message) newErrors[field] = message;
      });
    }

    const totalAmount = Number(formData.contributionAmount);

    const emiErrors = formData.emiSchedule.map((emi) => validateEMI(emi));
    const hasEmiErrors = emiErrors.some((err) => err.date || err.amount);

    if (hasEmiErrors) {
      newErrors.emiSchedule = emiErrors;
    } else if (formData.installment >= 1) {
      const sumOfEmis = formData.emiSchedule.reduce(
        (sum, emi) => sum + Number(emi.amount || 0),
        0
      );
      if (totalAmount > 0 && sumOfEmis !== totalAmount) {
        newErrors.emiScheduleSum = `The sum of installments (${sumOfEmis}) must equal the total contribution amount (${totalAmount})`;
      }
    }

    setErrors(newErrors);

    const hasFieldErrors = Object.keys(newErrors).some(
      (key) => key !== "emiSchedule" && newErrors[key]
    );
    const hasEmiFieldErrors =
      newErrors.emiSchedule && newErrors.emiSchedule.some((err) => err.date || err.amount);

    return !hasFieldErrors && !hasEmiFieldErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // stop submission, errors are now shown on screen
    }

    setSubmitting(true);
    setSubmitError("");

    const totalAmount = Number(formData.contributionAmount) || 0;

    const payload = {
      fund_type: formData.fundType || "OWN",
      donor_name: formData.name || "Alumni Donor",
      aadhar_no: formData.aadhar || "",
      pan_no: formData.ownPan || formData.csrPan || "",
      company_name: formData.companyName || "",
      contact_person: formData.contactPerson || "",
      contact_person_email: formData.contactPersonEmail || "",
      total_amount: totalAmount,
      no_of_installments: formData.installment || 1,
      installments: formData.emiSchedule.map((emi, idx) => ({
        installment_no: idx + 1,
        due_amount: Number(emi.amount) || 0,
        due_date: emi.date || new Date().toISOString().split("T")[0],
      })),
    };

    try {
      const response = await axios.post("/backend/payment-plans", payload);

      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        alert("Pledge Contribution Saved Successfully!");
        navigate("/pjc-dashboard", { replace: true });
        return;
      }
    } catch (error) {
      console.error("Error submitting contribution plan:", error);
      setSubmitError("Something went wrong while submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handles the case where the user chooses not to contribute at all —
  // this is its own short "form" with just a confirmation submit
  const handleNoContributeSubmit = (e) => {
    e.preventDefault();
    setNoContributeSubmitted(true);
  };

  // -------------------------------------------------- //
  // Render                                              //
  // -------------------------------------------------- //

  return (
    <div className="alumni-form">
      <h2>Fund Contribution Form</h2>

      <form onSubmit={handleSubmit} noValidate>
        {/* Basic details — fetched from DB, read-only */}
        <h3>Your Details</h3>

        {detailsLoading && <p>Loading your details...</p>}
        {detailsError && <span className="field-error">{detailsError}</span>}

        {alumniInfo && (
          <div className="alumni-details-readonly">
            <p><strong>Name:</strong> {alumniInfo.fullname}</p>
            <p><strong>Alumni:</strong> {alumniInfo.is_alumni ? "Yes" : "No"}</p>
            {alumniInfo.is_alumni && (
              <>
                <p><strong>Batch:</strong> {alumniInfo.batch}</p>
                <p><strong>Branch:</strong> {alumniInfo.branch}</p>
              </>
            )}
            <p><strong>Phone:</strong> {alumniInfo.phone}</p>
            <p><strong>Email:</strong> {alumniInfo.email}</p>
          </div>
        )}

        {alumniInfo && (
          <>
            {/* Step 1: Do you want to contribute? */}
            <label>Do you want to contribute?</label>

            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="contribute"
                  value="Yes"
                  checked={formData.contribute === "Yes"}
                  onChange={handleChange}
                />
                Yes
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="contribute"
                  value="No"
                  checked={formData.contribute === "No"}
                  onChange={handleChange}
                />
                No
              </label>
            </div>

            {formData.contribute === "No" && !noContributeSubmitted && (
              <button type="button" onClick={handleNoContributeSubmit}>
                Submit
              </button>
            )}

            {formData.contribute === "No" && noContributeSubmitted && (
              <h3 className="success">Form Submitted</h3>
            )}

            {/* Step 2: Are you an Alumni? */}

            {formData.contribute === "Yes" && (
              <>
                <label>Are you an Alumni?</label>

                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="alumni"
                      value="Yes"
                      checked={formData.alumni === "Yes"}
                      onChange={handleChange}
                    />
                    Yes
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="alumni"
                      value="No"
                      checked={formData.alumni === "No"}
                      onChange={handleChange}
                    />
                    No
                  </label>
                </div>
              </>
            )}

            {/* Alumni = Yes: full common details */}

            {formData.alumni === "Yes" && (
              <>
                <h3>Alumni Details</h3>
                <input
                  placeholder="Batch"
                  name="batch"
                  value={formData.batch}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.batch && <span className="field-error">{errors.batch}</span>}

                <input
                  placeholder="Branch"
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.branch && <span className="field-error">{errors.branch}</span>}

                <input
                  placeholder="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}

                <input
                  placeholder="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}

                <h3>Planned Contribution Amount</h3>
                <input
                  placeholder="Contribution Amount"
                  name="contributionAmount"
                  value={formData.contributionAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.contributionAmount && (
                  <span className="field-error">{errors.contributionAmount}</span>
                )}

                <label>Fund Type</label>

                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="fundType"
                      value="OWN"
                      checked={formData.fundType === "OWN"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    Own
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="fundType"
                      value="CSR"
                      checked={formData.fundType === "CSR"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    CSR
                  </label>
                </div>
                {errors.fundType && <span className="field-error">{errors.fundType}</span>}
              </>
            )}

            {/* OWN */}
            {formData.fundType === "OWN" && formData.contribute === "Yes" && !submitSuccess && (
              <>
                <h3>Own Contribution</h3>

                <input
                  placeholder="Aadhar Number"
                  name="aadhar"
                  value={formData.aadhar}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.aadhar && <span className="field-error">{errors.aadhar}</span>}

                <input
                  placeholder="PAN Number"
                  name="ownPan"
                  value={formData.ownPan}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.ownPan && <span className="field-error">{errors.ownPan}</span>}

                <label>Installments</label>
                <select value={formData.installment} onChange={handleInstallmentChange}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </>
            )}

            {/* CSR */}
            {formData.fundType === "CSR" && formData.contribute === "Yes" && !submitSuccess && (
              <>
                <h3>CSR Contribution</h3>

                <input
                  placeholder="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.companyName && <span className="field-error">{errors.companyName}</span>}

                <input
                  placeholder="Contact Person"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.contactPerson && <span className="field-error">{errors.contactPerson}</span>}

                <input
                  placeholder="Contact Person Email"
                  name="contactPersonEmail"
                  type="email"
                  value={formData.contactPersonEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.contactPersonEmail && (
                  <span className="field-error">{errors.contactPersonEmail}</span>
                )}

                <input
                  placeholder="Company PAN Number"
                  name="csrPan"
                  value={formData.csrPan}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.csrPan && <span className="field-error">{errors.csrPan}</span>}

                <label>Installments</label>
                <select value={formData.installment} onChange={handleInstallmentChange}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </>
            )}

            {/* EMI schedule */}
            {(formData.fundType === "OWN" || formData.fundType === "CSR") &&
              formData.contribute === "Yes" &&
              !submitSuccess && (
                <>
                  <h3>Contribution Schedule</h3>
                  {errors.emiScheduleSum && (
                    <span className="field-error" style={{ marginBottom: "16px" }}>
                      {errors.emiScheduleSum}
                    </span>
                  )}

                  {formData.emiSchedule.map((emi, index) => (
                    <div key={index} className="emi-card">
                      <h4>Contribution {index + 1}</h4>

                      <input
                        type="date"
                        value={emi.date}
                        onChange={(e) => handleEMIChange(index, "date", e.target.value)}
                        onBlur={(e) => handleEMIBlur(index, "date", e.target.value)}
                      />
                      {errors.emiSchedule?.[index]?.date && (
                        <span className="field-error">{errors.emiSchedule[index].date}</span>
                      )}

                      <input
                        type="number"
                        placeholder="Amount"
                        value={emi.amount}
                        onChange={(e) => handleEMIChange(index, "amount", e.target.value)}
                        onBlur={(e) => handleEMIBlur(index, "amount", e.target.value)}
                      />
                      {errors.emiSchedule?.[index]?.amount && (
                        <span className="field-error">{errors.emiSchedule[index].amount}</span>
                      )}
                    </div>
                  ))}
                </>
              )}

            {submitError && <span className="field-error">{submitError}</span>}

            {submitSuccess && <h3 className="success">Contribution Submitted Successfully</h3>}

            {formData.contribute === "Yes" &&
              (formData.fundType === "OWN" || formData.fundType === "CSR") &&
              !submitSuccess && (
                <button type="submit" disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              )}
          </>
        )}
      </form>
    </div>
  );
};

export default AlumniContributionForm;